import {
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    HostBinding,
    Input,
    Output,
    ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
    selector: 'app-chips-input',
    templateUrl: './chips-input.component.html',
    styleUrls: ['./chips-input.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ChipsInputComponent),
            multi: true,
        },
    ],
    host: {
        '[class.p-focus]': 'focused',
        '[class.error]': 'invalid',
    },
    standalone: false,
})
export class ChipsInputComponent implements ControlValueAccessor {
    @Input() placeholder = '';
    @Input() inputId?: string;
    @Input() addOnBlur: any = true;
    @Input() allowDuplicate: any = false;
    @Input() separator: any = ',';
    @Input() invalid = false;
    /** False keeps spaces inside a token, for values such as group names. Commas and line breaks still separate. */
    @Input() splitOnWhitespace = true;

    /** Optional panel/context class(es) from parent for scoped CSS (e.g. "transparency-pod"). */
    @Input() panelClass: string | null = null;

    @HostBinding('class') get hostClasses(): string {
        const base = 'p-chips p-component p-input-wrapper';
        return this.panelClass ? `${base} ${this.panelClass}`.trim() : base;
    }

    // PrimeNG `p-chips` supports `[disabled]` as an input; we support it too.
    @Input() set disabled(value: any) {
        this.disabledInput = this.coerceBooleanProperty(value);
    }

    @Output() onAdd = new EventEmitter<any>();
    @Output() onRemove = new EventEmitter<any>();
    @Output() onModelChange = new EventEmitter<string[]>();
    @Output() onBlur = new EventEmitter<FocusEvent>();
    @Output() keyup = new EventEmitter<KeyboardEvent>();

    @ViewChild('chipInput', { static: true }) chipInput!: ElementRef<HTMLInputElement>;

    value: string[] = [];
    focused = false;

    private disabledCva = false;
    private disabledInput = false;

    private onChange: (value: string[]) => void = () => {};
    private onTouched: () => void = () => {};

    get isDisabled(): boolean {
        return this.disabledCva || this.disabledInput;
    }

    writeValue(val: any): void {
        if (!val) {
            this.value = [];
            return;
        }
        if (Array.isArray(val)) {
            this.value = [...val];
            return;
        }
        // fallback: accept string -> split into tokens
        this.value = this.splitIntoTokens(String(val));
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabledCva = isDisabled;
    }

    focusInput() {
        if (this.isDisabled) return;
        this.chipInput?.nativeElement?.focus();
    }

    onFocus() {
        this.focused = true;
    }

    handleBlur(event?: FocusEvent) {
        this.focused = false;
        this.onTouched();
        if (this.coerceBooleanProperty(this.addOnBlur)) {
            this.commitFromInput();
        }
        this.onBlur.emit(event as any);
    }

    onPaste(event: ClipboardEvent) {
        if (this.isDisabled) return;
        const text = event.clipboardData?.getData('text') ?? '';
        if (!text) return;
        event.preventDefault();
        this.addTokens(this.splitIntoTokens(text));
        this.clearInput();
    }

    onKeydown(event: KeyboardEvent) {
        if (this.isDisabled) return;

        const input = this.chipInput?.nativeElement;
        const current = input?.value ?? '';
        const key = (event.key ?? '').toLowerCase();

        if (key === 'backspace' && !current) {
            if (this.value.length) {
                this.removeAtIndex(this.value.length - 1, event);
            }
            return;
        }

        const sep = String(this.separator ?? ',');
        const isSeparatorKey =
            key === 'enter' ||
            (key === ' ' && this.splitOnWhitespace) ||
            (sep.includes(',') && key === ',') ||
            (sep.includes(';') && key === ';');

        if (isSeparatorKey) {
            event.preventDefault();
            event.stopPropagation();
            this.commitFromInput();
        }
    }

    removeAtIndex(index: number, event?: Event) {
        if (this.isDisabled) return;
        event?.preventDefault?.();
        event?.stopPropagation?.();

        if (index < 0 || index >= this.value.length) return;
        const removed = this.value[index];
        this.value = this.value.filter((_, i) => i !== index);
        this.propagateModel();
        this.onRemove.emit({ value: removed, index });
        this.focusInput();
    }

    private commitFromInput() {
        const input = this.chipInput?.nativeElement;
        const raw = input?.value ?? '';
        const tokens = this.splitIntoTokens(raw);
        if (tokens.length) {
            this.addTokens(tokens);
        }
        this.clearInput();
    }

    private addTokens(tokens: string[]) {
        const allowDup = this.coerceBooleanProperty(this.allowDuplicate);
        const added: string[] = [];
        for (const token of tokens) {
            const trimmed = token.trim();
            if (!trimmed) continue;
            if (!allowDup && this.value.includes(trimmed)) continue;
            this.value = [...this.value, trimmed];
            added.push(trimmed);
        }
        if (added.length) {
            this.propagateModel();
            // mimic PrimeNG-ish event shape
            this.onAdd.emit({ value: added.length === 1 ? added[0] : added });
        }
    }

    private propagateModel() {
        const next = [...this.value];
        this.onChange(next);
        this.onModelChange.emit(next);
    }

    private clearInput() {
        const input = this.chipInput?.nativeElement;
        if (input) input.value = '';
    }

    private splitIntoTokens(text: string): string[] {
        if (!text) return [];
        // support whitespace + commas regardless of `separator` to match prior UX
        return text
            .split(this.splitOnWhitespace ? /[,\s]+/g : /[,\r\n]+/g)
            .map((t) => t.trim())
            .filter(Boolean);
    }

    private coerceBooleanProperty(value: any): boolean {
        // matches Angular's common template behavior: `disabled=""` should be true
        return value != null && `${value}` !== 'false';
    }
}

